import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { AnswerChangeDetector } from '../helpers/answer-change-detector';
import { Answer } from '../models/answers/answer.model';

/**
 * Service för att hämta tidigare svar för att kunna visa ändringsmarkering
 * kan användas på alla frågetyper, förutom projektparer eller budget
 */
@Injectable({
  providedIn: 'root',
})
export class PreviousAnswersService {
  private previousAnswersSubject = new BehaviorSubject<Answer[]>([]);
  public previousAnswers$: Observable<Answer[]> =
    this.previousAnswersSubject.asObservable();

  setPreviousAnswers(answers: Answer[]): void {
    this.previousAnswersSubject.next(answers || []);
  }

  getPreviousAnswer(
    questionId: string,
    repeaterGroupId?: string,
  ): Answer | undefined {
    const answers = this.previousAnswersSubject.value;
    return answers.find(
      (a) =>
        a.questionId === questionId &&
        (repeaterGroupId
          ? a.repeaterGroupId === repeaterGroupId
          : !a.repeaterGroupId),
    );
  }

  getPreviousValue(
    questionId: string,
    repeaterGroupId?: string,
  ): string | undefined {
    const answer = this.getPreviousAnswer(questionId, repeaterGroupId);
    if (!answer?.value) {
      return undefined;
    }

    try {
      const parsed = JSON.parse(answer.value);
      if (Array.isArray(parsed) && parsed.length === 1) {
        return String(parsed[0]);
      }
      return answer.value;
    } catch {
      return answer.value;
    }
  }

  clear(): void {
    this.previousAnswersSubject.next([]);
  }

  /**
   * Generisk jämförelse för frågetyper med ett enkelt strängvärde
   * (input, dropdown, checkbox, radiobutton, datepicker utan intervall).
   */
  hasSimpleValueChanged(
    questionId: string,
    repeaterGroupId: string | undefined,
    currentValue: string | undefined,
    includeRemoval = true,
  ): boolean {
    const previousValue = this.getPreviousValue(questionId, repeaterGroupId);
    return AnswerChangeDetector.simple(
      previousValue,
      currentValue,
      includeRemoval,
    );
  }

  /**
   * Checkbox (flerval) - returnerar id:n som är ikryssade nu men inte var det
   * i föregående svar, så att markeringen kan visas bredvid varje ändrat
   * alternativ istället för en enda markering för hela frågan.
   */
  getChangedCheckboxIds(
    questionId: string,
    repeaterGroupId: string | undefined,
    currentValue: string | undefined,
  ): string[] {
    return AnswerChangeDetector.checkboxIds(
      this.getPreviousAnswer(questionId, repeaterGroupId)?.value,
      currentValue,
    );
  }

  /**
   * För frågetyper där själva svaret är ett JSON-objekt med flera delfält
   * (t.ex. textarea med översättning: valueSwedish/valueEnglish). Jämför ETT
   * fält åt gången mot motsvarande fält i det tidigare svaret.
   */
  hasFieldChanged(
    questionId: string,
    repeaterGroupId: string | undefined,
    field: string,
    currentValue: string | undefined,
    includeRemoval = true,
  ): boolean {
    const previousValue = this.getPreviousAnswer(
      questionId,
      repeaterGroupId,
    )?.value;
    return AnswerChangeDetector.field(
      previousValue,
      field,
      currentValue,
      includeRemoval,
    );
  }

  /**
   * För frågetyper där svaret är en lista av stabila id:n som ska jämföras
   * oordnat (t.ex. classification per typ, file-upload). `extractPreviousIds`
   * plockar ut rätt id-lista ur det uppparsade tidigare svaret.
   */
  private hasSortedListChanged(
    questionId: string,
    repeaterGroupId: string | undefined,
    currentIds: (string | number)[],
    extractPreviousIds: (parsedPreviousValue: unknown) => (string | number)[],
  ): boolean {
    const previousValue = this.getPreviousAnswer(
      questionId,
      repeaterGroupId,
    )?.value;
    return AnswerChangeDetector.list(
      previousValue,
      currentIds,
      extractPreviousIds,
    );
  }

  private tryParseJson(
    value: string,
  ): { valueSwedish?: unknown; valueEnglish?: unknown } | undefined {
    try {
      return JSON.parse(value);
    } catch {
      return undefined;
    }
  }

  /**
   * Textarea (utan översättning) - previousValue kan ändå vara ett JSON-objekt
   * med valueSwedish/valueEnglish om frågan tidigare hade översättning påslagen.
   */
  hasTextareaValueChanged(
    questionId: string,
    repeaterGroupId: string | undefined,
    currentValue: string | undefined,
  ): boolean {
    const previousValue = this.getPreviousValue(questionId, repeaterGroupId);
    const parsed = previousValue ? this.tryParseJson(previousValue) : undefined;

    if (
      parsed &&
      (parsed.valueSwedish !== undefined || parsed.valueEnglish !== undefined)
    ) {
      return false;
    }

    return this.hasSimpleValueChanged(
      questionId,
      repeaterGroupId,
      currentValue,
    );
  }

  /**
   * Classification - jämför valda id:n för en specifik typ (BO/FO/PO) mot
   * föregående svar. `currentItems` är de valda objekten för just den typen.
   */
  hasClassificationTypeChanged(
    questionId: string,
    repeaterGroupId: string | undefined,
    currentItems: { identifier: string }[] | undefined,
    typeKey: string,
  ): boolean {
    const currentIdentifiers = (currentItems ?? [])
      .map((item) => item.identifier)
      .sort();

    return this.hasSortedListChanged(
      questionId,
      repeaterGroupId,
      currentIdentifiers,
      (parsedPrevious) =>
        (
          (
            parsedPrevious as Record<
              string,
              { choosenItems?: { identifier: string }[] }
            >
          )?.[typeKey]?.choosenItems ?? []
        ).map((item) => item.identifier),
    );
  }

  /**
   * File-upload - returnerar uploadId för filer som är nya sedan föregående
   * svar, så markeringen kan visas bredvid varje ny fil istället för en enda
   * markering för hela frågan. Jämförs på uploadId eftersom både fileId och
   * pdfFileId genereras om varje gång en fil kopieras mellan versioner.
   */
  getChangedFileUploadIds(
    questionId: string,
    repeaterGroupId: string | undefined,
    currentFiles: { uploadId?: string }[],
  ): string[] {
    const previousValue = this.getPreviousAnswer(
      questionId,
      repeaterGroupId,
    )?.value;
    return AnswerChangeDetector.addedIds(
      previousValue,
      currentFiles,
      (file) => file.uploadId,
    );
  }

  /**
   * Person - person-id återskapas varje gång en ansökan/komplettering skapas
   * från Effekta-data, så vi matchar på listposition (index) istället för id.
   */
  hasPersonFieldChanged(
    questionId: string,
    repeaterGroupId: string | undefined,
    personIndex: number,
    field: string,
    currentValue: string | undefined,
  ): boolean {
    const previousValue = this.getPreviousAnswer(
      questionId,
      repeaterGroupId,
    )?.value;
    return AnswerChangeDetector.personField(
      previousValue,
      personIndex,
      field,
      currentValue,
    );
  }

  /**
   * Datepicker (enkelt datum, inte intervall).
   */
  hasDateValueChanged(
    questionId: string,
    repeaterGroupId: string | undefined,
    currentValue: unknown,
  ): boolean {
    const previousValue = this.getPreviousValue(questionId, repeaterGroupId);
    return AnswerChangeDetector.date(previousValue, currentValue);
  }

  /**
   * Datepicker (intervall) - jämför fromDate/toDate mot motsvarande fält i
   * det tidigare svaret.
   */
  hasDateRangeFieldChanged(
    questionId: string,
    repeaterGroupId: string | undefined,
    field: 'fromDate' | 'toDate',
    currentValue: unknown,
  ): boolean {
    const previousValue = this.getPreviousValue(questionId, repeaterGroupId);
    return AnswerChangeDetector.dateField(previousValue, field, currentValue);
  }
}
